/* eslint-disable node/no-unsupported-features/node-builtins */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BASE_LAYOUT from "../components/layout/BaseLayout.component";
import { ViewContainer } from "./View.styles";
import { RootState } from "../state-management/root-reducer";
import BASE_MAP from "../components/map/BaseMap.component";
import { IViewProps } from "./View.types";
import DRAWER from "../components/drawer/Drawer.component";
import AppMedia from "../theme/utils/AppMedia";
import {
  clearSelectedMarker,
  updateAdminModeState,
  setLoggedIn,
  setOutageNotFound,
  updatePinModeState,
  setRoutingOutageId,
  updateDrawerState,
  setShowAdminModal,
  setOriginatorFlag,
  setPinBracketType,
  setEntraAuthstate,
} from "../state-management/slices/global/global.slice";
import { AppDispatch } from "../state-management/store";
import { BaseMapService } from "../components/map/BaseMap.service";
import {
  clearImageData,
  clearOutageError,
} from "../state-management/slices/outages/outages.slice";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import PIN_MODE_MODAL from "../components/modal/PinMode.modal";
import { getGlobalState } from "../shared/utils/testing.utils";
import { ClickEvent } from "../shared/types/event.types";
import useQuery from "../shared/hooks/useQuery";
import OUTAGE_NOT_FOUND_MODAL from "../components/modal/OutageNotFound.modal";
import OUTAGE_RECONCILIATION_MODAL from "../components/modal/outage-reconciliation/OutageReconciliation.modal";
import UIMessages from "../state-management/localStorage/UIMessages";
import { fetchAdminOutages } from "../state-management/slices/outages/outages.thunk";
import { FormService } from "../components/form/Form.service";
import { AuthSericeEntra } from "../shared/services/Auth.service.entra";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

const onDrawerCloseClick = (
  event: ClickEvent,
  dispatch: AppDispatch,
  baseMapService: BaseMapService,
  formService: FormService
) => {
  event.preventDefault();
  const mapView = baseMapService.getMapView();
  const featureLayerService = baseMapService.getFeatureLayerService();
  const uiState = getGlobalState().ui;

  formService.resetForm();

  dispatch(updateDrawerState(false));
  dispatch(
    setPinBracketType({
      bracketType: null,
    })
  );
  dispatch(clearOutageError());
  dispatch(clearSelectedMarker());
  dispatch(clearImageData());

  if (uiState.pinMode.enabled) {
    featureLayerService.removePinModeGraphics();
    featureLayerService.fadeOverlay("off");
    dispatch(updatePinModeState(false));
  } else {
    baseMapService.removeMapViewGraphics();
  }

  mapView.popup.close();
  baseMapService.processExtent();
};

const setLocalStorage = () => {
  const messages = UIMessages.getInstance();
  messages.setPinModeAccepted("true");
};

const MAP_VIEW: React.FC<IViewProps> = (props: IViewProps) => {
  const { Media } = AppMedia;
  const dispatch = useDispatch();
  const query = useQuery();
  const sendDataToGTM = useGTMDispatch();
  const { instance, accounts } = useMsal();

  const messagesLocalStorage = UIMessages.getInstance();

  // Services
  const authServiceEntra = AuthSericeEntra.getInstance(dispatch, instance);
  const baseMapService = BaseMapService.getInstance(dispatch, sendDataToGTM);
  const formService: FormService = FormService.getInstance(
    dispatch,
    sendDataToGTM
  );

  // Local State
  const [showPinModeModal, setShowPinModeModal] = useState(false);
  const [pinModeAccepted, setPinModeAccepted] = useState(false);
  const [tokenParsed, setTokenParsed] = useState(false);

  // App State
  const { ui, auth, appConfig } = useSelector(
    (state: RootState) => state.globalState
  );
  const { isDrawerOpen, routing, loading, adminMode } = ui;
  const { outageNotFound } = routing;
  const isAuthenticated = useIsAuthenticated();

  // Query Params
  const adminModeQuery = query.get("admin_mode");
  const outageIdQuery = query.get("outageId");
  const loggedInQuery = query.get("logged_in");
  const originatorQuery = query.get("originator");
  const originatorQueryUppercase = query.get("Originator");
  const callbackCodeQuery = query.get("code");
  const callbackStateQuery = query.get("state");

  useEffect(() => {
    if (adminModeQuery && JSON.parse(adminModeQuery) && appConfig) {
      authServiceEntra.signin(appConfig);
    }

    const { outageId } = routing;
    const pinModeAcceptedState = JSON.parse(
      messagesLocalStorage.getPinModeAccepted() as string
    );

    if (originatorQuery || originatorQueryUppercase) {
      if (originatorQuery) dispatch(setOriginatorFlag(originatorQuery));
      if (originatorQueryUppercase)
        dispatch(setOriginatorFlag(originatorQueryUppercase));
    }

    if (pinModeAcceptedState !== pinModeAccepted) {
      setPinModeAccepted(pinModeAcceptedState);
    }

    if (outageIdQuery) {
      if (!outageId || outageIdQuery !== outageId) {
        dispatch(setRoutingOutageId(outageIdQuery));
      }
    } else {
      setShowPinModeModal(true);
    }

    if (loggedInQuery && !auth.loggedIn) {
      if (JSON.parse(loggedInQuery)) {
        if (callbackCodeQuery && callbackStateQuery && !tokenParsed) {
          setTokenParsed(true);
        }
      } else {
        dispatch(setLoggedIn(false));
        dispatch(updateAdminModeState(false));
      }
    }
    console.log(isAuthenticated, !auth.loggedIn);
    if (isAuthenticated && !auth.loggedIn) {
      if (outageIdQuery) {
        return;
      }
      const accessTokenRequest = {
        scopes: [`api://${appConfig?.azureClientId}/${appConfig?.azureScope}`],
        account: accounts[0],
      };
      instance
        .acquireTokenSilent(accessTokenRequest)
        .then((accessToken) => {
          const roles: string[] = JSON.parse(
            JSON.stringify(accessToken.idTokenClaims)
          )?.roles;
          if (
            roles &&
            roles.findIndex((r) => r.indexOf("SoarAdmin") >= 0) >= 0
          ) {
            dispatch(setEntraAuthstate(accessToken));
            dispatch(fetchAdminOutages());
            dispatch(updateAdminModeState(true));
            dispatch(updateDrawerState(true));
          }
        })
        .catch((error) => {
          console.log("Access Token Error:", error);
        });

      dispatch(setLoggedIn(true));
    }
  }, [
    adminModeQuery,
    auth.loggedIn,
    isAuthenticated,
    authServiceEntra,
    callbackCodeQuery,
    callbackStateQuery,
    instance,
    appConfig,
    accounts,
    dispatch,
    loggedInQuery,
    messagesLocalStorage,
    originatorQuery,
    originatorQueryUppercase,
    outageIdQuery,
    pinModeAccepted,
    routing,
    tokenParsed,
  ]);

  return (
    <>
      {/* --------------------------------- */}
      {/* OUTAGE NOT FOUND MODAL ---------- */}
      {/* --------------------------------- */}
      <OUTAGE_NOT_FOUND_MODAL
        show={outageNotFound}
        handleClose={() => {
          dispatch(setOutageNotFound(false));
          setShowPinModeModal(true);
        }}
      />

      {/* --------------------------------- */}
      {/* PIN MODE MODAL ------------------ */}
      {/* --------------------------------- */}
      <PIN_MODE_MODAL
        show={showPinModeModal && !pinModeAccepted && !loading}
        handleClose={() => {
          setLocalStorage();
          setShowPinModeModal(false);
        }}
      />

      {/* --------------------------------- */}
      {/* ADMIN MODE MODAL ------------------ */}
      {/* --------------------------------- */}
      <OUTAGE_RECONCILIATION_MODAL
        show={adminMode.showAdminModal}
        centered
        handleClose={() => {
          dispatch(setShowAdminModal({ show: false }));
        }}
      />

      {/* --------------------------------- */}
      {/* DRAWER (MOBILE) ----------------- */}
      {/* --------------------------------- */}
      <Media lessThan="tablet">
        <DRAWER
          isDrawerOpen={isDrawerOpen}
          isMobile={true}
          onClose={(event) =>
            onDrawerCloseClick(event, dispatch, baseMapService, formService)
          }
        />
      </Media>

      {/* --------------------------------- */}
      {/* DRAWER (DESKTOP) ---------------- */}
      {/* --------------------------------- */}
      <Media greaterThanOrEqual="tablet">
        <DRAWER
          isDrawerOpen={isDrawerOpen}
          isMobile={false}
          onClose={(event) =>
            onDrawerCloseClick(event, dispatch, baseMapService, formService)
          }
        />
      </Media>

      {/* --------------------------------- */}
      {/* BASE MAP ------------------------ */}
      {/* --------------------------------- */}
      <BASE_LAYOUT data-testid="map-view">
        <ViewContainer fluid className="cms-soar-view-container">
          <BASE_MAP />
        </ViewContainer>
      </BASE_LAYOUT>
    </>
  );
};

export default MAP_VIEW;
